import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { PageHeader } from '@/components/page-header';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  addPasskey,
  confirmPasskey,
  listPasskeys,
  type Passkey,
  removePasskey,
} from '@/lib/auth';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/_app/security')({
  component: SecurityPage,
});

const PASSKEYS = ['staff-passkeys'] as const;

/** The signed-in person's own second factors: the authenticator app and passkeys. */
function SecurityPage() {
  return (
    <>
      <PageHeader
        title="My security"
        description="How you prove it is you when the portal asks."
      />
      <PasskeyCard />
    </>
  );
}

function PasskeyCard() {
  const client = useQueryClient();
  const passkeys = useQuery({ queryKey: PASSKEYS, queryFn: listPasskeys });
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [removing, setRemoving] = useState<Passkey | null>(null);
  const add = useMutation({
    mutationFn: () => addPasskey(name, code),
    onSuccess: async () => {
      setName('');
      setCode('');
      await client.invalidateQueries({ queryKey: PASSKEYS });
      await client.invalidateQueries({ queryKey: ['staff-has-passkey'] });
      toast.success('Passkey added and confirmed.');
    },
    onError: () => client.invalidateQueries({ queryKey: PASSKEYS }),
  });
  const confirm = useMutation({
    mutationFn: (id: string) => confirmPasskey(id, code),
    onSuccess: async () => {
      setCode('');
      await client.invalidateQueries({ queryKey: PASSKEYS });
      await client.invalidateQueries({ queryKey: ['staff-has-passkey'] });
      toast.success('Passkey confirmed.');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Passkeys</CardTitle>
        <CardDescription>
          Sign in with Face ID, Touch ID, Windows Hello, your device PIN or a
          security key instead of a password and a code. Your password and
          authenticator app keep working as a back-up. A new passkey counts only
          once you confirm it with a fresh authenticator code.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {passkeys.isError ? (
          <Alert variant="destructive">
            Could not load your passkeys. Reload the page.
          </Alert>
        ) : null}
        {passkeys.data?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You have no passkey yet.
          </p>
        ) : null}
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
          {(passkeys.data ?? []).map((passkey) => (
            <li
              key={passkey.id}
              className="flex items-center justify-between gap-3 p-3"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">
                  {passkey.name}
                  {passkey.confirmed ? null : (
                    <span className="ml-2 text-xs font-normal text-destructive">
                      Not confirmed: it can't sign you in
                    </span>
                  )}
                </p>
                {passkey.createdAt ? (
                  <p className="text-xs text-muted-foreground">
                    Added {formatDateTime(passkey.createdAt)}
                    {passkey.lastUsedAt
                      ? ` · Last used ${formatDateTime(passkey.lastUsedAt)}`
                      : ''}
                  </p>
                ) : null}
              </div>
              <div className="flex gap-2">
                {passkey.confirmed ? null : (
                  <Button
                    size="sm"
                    pending={confirm.isPending}
                    disabled={code.length < 6}
                    onClick={() => confirm.mutate(passkey.id)}
                  >
                    Confirm with the code
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRemoving(passkey)}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2">
          <Label htmlFor="passkey-code">Authenticator code</Label>
          <Input
            id="passkey-code"
            className="w-full sm:w-40"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6 digits"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
          />
          <Label htmlFor="passkey-name">Name for the new passkey</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="passkey-name"
              className="w-full sm:w-64"
              placeholder="For example, Work laptop"
              maxLength={60}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Button
              pending={add.isPending}
              disabled={code.length < 6}
              onClick={() => add.mutate()}
            >
              Add a passkey
            </Button>
          </div>
          {add.error || confirm.error ? (
            <Alert variant="destructive">
              {(add.error ?? confirm.error)?.message}
            </Alert>
          ) : null}
        </div>
      </CardContent>
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title={`Remove ${removing?.name ?? 'this passkey'}`}
        body="You will no longer be able to sign in with it. Your password and authenticator app still work."
        confirmLabel="Remove"
        tone="destructive"
        onConfirm={async () => {
          if (!removing) return;
          await removePasskey(removing.id);
          setRemoving(null);
          await client.invalidateQueries({ queryKey: PASSKEYS });
          await client.invalidateQueries({ queryKey: ['staff-has-passkey'] });
          toast.success('Passkey removed.');
        }}
      />
    </Card>
  );
}
